// Modal.jsx
import { useState, useRef, useEffect } from 'react';

function Modal({ 
  title, 
  onClose, 
  children, 
  className = "",
  draggable = true,  // DEFAULT: true
  closeOnBackdropClick = true,  // DEFAULT: true
  closeOnEsc = true,  // DEFAULT: true
  constrainToViewport = true,  // DEFAULT: true
  position = 'center',  // DEFAULT: 'center'
  initialPosition = null  // DEFAULT: null
}) {
  const [isDragging, setIsDragging] = useState(false);
  const [currentPosition, setCurrentPosition] = useState({ x: 0, y: 0 });
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const modalRef = useRef(null);
  const backdropRef = useRef(null);

  // Calcola la posizione iniziale in base ai parametri
  const getInitialPosition = () => {
    // Priorità 1: initialPosition custom
    if (initialPosition) {
      return initialPosition;
    }

    // Priorità 2: position preset
    // La modale è già centrata dal flexbox, quindi questi sono offset dal centro
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    switch (position) {
      case 'top':
        // Sposta verso l'alto dal centro (y negativo)
        return { x: 0, y: -viewportHeight * 0.35 };
      case 'top-right':
        return { x: viewportWidth * 0.3, y: -viewportHeight * 0.35 };
      case 'top-left':
        return { x: -viewportWidth * 0.3, y: -viewportHeight * 0.35 };
      case 'bottom':
        // Sposta verso il basso dal centro (y positivo)
        return { x: 0, y: viewportHeight * 0.25 };
      case 'bottom-right':
        return { x: viewportWidth * 0.3, y: viewportHeight * 0.25 };
      case 'bottom-left':
        return { x: -viewportWidth * 0.3, y: viewportHeight * 0.25 };
      case 'center':
      default:
        return { x: 0, y: 0 };
    }
  };

  // Reset position quando la modale si apre/chiude
useEffect(() => {
  // Prima imposta la posizione di default
  const tempPos = getInitialPosition();
  setCurrentPosition(tempPos);
  
  // Poi ricalcola DOPO il render quando conosci le dimensioni reali
  setTimeout(() => {
    if (modalRef.current && position.startsWith('top')) {
      const modalHeight = modalRef.current.offsetHeight;
      const viewportHeight = window.innerHeight;
      const viewportWidth = window.innerWidth;
      const topMargin = 220;
      
      // Calcola yOffset per posizionare il top a topMargin
      let yOffset = (topMargin + modalHeight / 2) - (viewportHeight / 2);
      
      // CRITICAL: Se yOffset risulta negativo, significa che la modale 
      // andrebbe sopra il centro. Impostiamolo a un valore che garantisce
      // che il top della modale sia sempre a topMargin dal top del viewport
      const minYOffset = -(viewportHeight / 2 - modalHeight / 2 - topMargin);
      yOffset = Math.max(minYOffset, yOffset);
      
      // Calcola xOffset per posizioni angolari
      let xOffset = 0;
      if (position === 'top-left') {
        xOffset = -viewportWidth * 0.3;
      } else if (position === 'top-right') {
        xOffset = viewportWidth * 0.3;
      }
      /*
      console.log('RECALCULATED:', { 
        modalHeight, 
        viewportHeight, 
        topMargin, 
        yOffset,
        xOffset,
        minYOffset 
      });
      */
      setCurrentPosition({ x: xOffset, y: yOffset });
    }
  }, 0);
}, []);

  // Gestione ESC key
  useEffect(() => {
    if (!closeOnEsc) return;

    const handleEscKey = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscKey);
    return () => document.removeEventListener('keydown', handleEscKey);
  }, [closeOnEsc, onClose]);

const constrainPosition = (x, y) => {
  if (!constrainToViewport || !modalRef.current) {
    return { x, y };
  }

  const modalRect = modalRef.current.getBoundingClientRect();
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;
  const margin = 50;
  const topMargin = 10;
  const bottomMargin = 20; // Margine minimo in basso

  // Calcola dove si troverebbe la modale con il nuovo offset
  const currentCenterX = modalRect.left + modalRect.width / 2;
  const currentCenterY = modalRect.top + modalRect.height / 2;
  
  const newCenterX = currentCenterX + (x - currentPosition.x);
  const newCenterY = currentCenterY + (y - currentPosition.y);
  
  // Limiti orizzontali
  const minCenterX = margin;
  const maxCenterX = viewportWidth - margin;
  
  // Limiti verticali: considera l'altezza effettiva della modale
  const modalHeight = modalRect.height;
  const minCenterY = topMargin + modalHeight / 2;
  const maxCenterY = viewportHeight - bottomMargin - modalHeight / 2;
  
  const constrainedCenterX = Math.max(minCenterX, Math.min(maxCenterX, newCenterX));
  const constrainedCenterY = Math.max(minCenterY, Math.min(maxCenterY, newCenterY));
  
  const constrainedX = x + (constrainedCenterX - newCenterX);
  const constrainedY = y + (constrainedCenterY - newCenterY);

  return { x: constrainedX, y: constrainedY };
};
  const handleMouseDown = (e) => {
    if (!draggable) return;
    
    setIsDragging(true);
    setDragStart({
      x: e.clientX - currentPosition.x,
      y: e.clientY - currentPosition.y
    });
  };

  const handleMouseMove = (e) => {
    if (!isDragging || !draggable) return;

    const newX = e.clientX - dragStart.x;
    const newY = e.clientY - dragStart.y;

    const constrainedPos = constrainPosition(newX, newY);
    setCurrentPosition(constrainedPos);
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  // Gestione click sul backdrop
  const handleBackdropClick = (e) => {
    if (!closeOnBackdropClick) return;
    
    // Chiudi solo se il click è direttamente sul backdrop, non sulla modale
    if (e.target === backdropRef.current) {
      onClose();
    }
  };

  // Aggiungi listeners globali per il drag
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, dragStart, currentPosition]);

  
  const modalStyle = draggable ? {
    display: "flex",
    transform: `translate(${currentPosition.x}px, ${currentPosition.y}px)`,
    cursor: isDragging ? 'grabbing' : 'auto'
  } : {
    display: "flex"
  };

  const titleStyle = draggable ? {
    cursor: 'grab',
    userSelect: 'none'
  } : {};

  const backdropStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000
  };

  return (
    <div 
      ref={backdropRef}
      style={backdropStyle}
      onClick={handleBackdropClick}
    >
      <div 
        ref={modalRef}
        className={`modal ${className}`} 
        style={modalStyle}
      >
        <div className="btn_cerrar" onClick={onClose}>
          <i className="bi bi-x-square"></i>
        </div>
        <span 
          className="modal_tittle"
          style={titleStyle}
          onMouseDown={handleMouseDown}
        >
          {title}
        </span>
        {children}
      </div>
    </div>
  );
}

export default Modal;